import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import {
  SdiDashboard,
  SdiLicense,
  SdiProfile,
  SdiShip,
  SdirFooter,
  SdirHeader,
  SkipLink,
  styles,
} from '@sdir/sds';
import { useAuthentication, useCurrentUser } from '@sdir/auth/lib/hooks';
import { isToggleOn } from '@sdir/blueprint.aps/lib/helpers/permissionUtilities';
import { routes } from '../../../constants/routes';
import {
  useCurrentPerson,
  useCurrentRegisteredPerson,
  useFeatureToggles,
} from '../../../services/hooks';
import {
  ToggleApplications,
  ToggleCertificates,
  ToggleDisableLanguage,
  ToggleDisableSid,
  ToggleEndorsement,
  TogglePaymentOverview,
  ToggleProfile,
} from '../../../constants/permissions';
import {
  DEFAULT_LANG,
  getLanguageOptions,
  navigateDirectlyByUrl,
} from '../../../helpers/languageHelpers';
import { isValidNorwegianPnr } from '../../../helpers/userHelpers';

type AppLayoutProps = {
  children: any;
  onChangeLanguage: (l: string) => void;
};

export const AppLayout: React.FC<AppLayoutProps> = ({ children, onChangeLanguage }) => {
  const intl = useIntl();
  const currentUser = useCurrentUser();
  const authenticationService = useAuthentication();
  const featureToggle = useFeatureToggles();
  const {
    person: { result: contactInfo },
  } = useCurrentPerson();
  const {
    person: { result: registeredPerson },
  } = useCurrentRegisteredPerson();

  const hasValidPnr = useMemo(() => {
    return isValidNorwegianPnr(
      contactInfo?.personalIdentificationNumber || registeredPerson?.personNumber || ''
    );
  }, [contactInfo, registeredPerson]);

  const onLogout = useCallback(() => {
    authenticationService?.logout();
  }, []);

  const languageOptions = useMemo(() => {
    return getLanguageOptions();
  }, []);

  const userPersonalNr = useMemo(() => {
    return contactInfo?.personalIdentificationNumber || '';
  }, [contactInfo]);

  const { loggedInAsTitle, loggedInAsDropdownInfo } = useMemo(() => {
    const username = currentUser?.profile?.name || '';
    const identification = currentUser?.profile?.reportee || '';
    return {
      loggedInAsTitle: (
        <span>
          {intl.formatMessage({ id: 'userbar.loggedin' })}
          <span className="large-screens-only">
            {' '}
            {intl.formatMessage({ id: 'userbar.as' })}{' '}
            <strong>
              {username} ({identification})
            </strong>
          </span>
        </span>
      ),
      loggedInAsDropdownInfo: (
        <p>
          {intl.formatMessage({ id: 'userbar.otherUser' })}
          <strong>
            {username} ({identification})
          </strong>{' '}
          {intl.formatMessage({ id: 'userbar.otherUser.info' })}
        </p>
      ),
    };
  }, [currentUser?.profile, intl]);

  const showCertificate = !isToggleOn(featureToggle?.result, ToggleCertificates, userPersonalNr);
  const showApplications = !isToggleOn(featureToggle?.result, ToggleApplications, userPersonalNr);
  const showProfile = !isToggleOn(featureToggle?.result, ToggleProfile, userPersonalNr);
  const showPayment = !isToggleOn(featureToggle?.result, TogglePaymentOverview, userPersonalNr);
  const showLanguage = !isToggleOn(featureToggle?.result, ToggleDisableLanguage, userPersonalNr);
  const showEndorsement = !isToggleOn(featureToggle?.result, ToggleEndorsement, userPersonalNr);
  const showSid = !isToggleOn(featureToggle?.result, ToggleDisableSid, userPersonalNr);

  const aboutLinks = useMemo(() => {
    return [
      {
        text: intl.formatMessage({ id: 'footer.about.privacy' }),
        url: 'https://www.sdir.no/om-oss/personvernerklaring/',
      },
      {
        text: intl.formatMessage({ id: 'footer.about.accessibility' }),
        url: 'https://uustatus.no/nb/erklaringer/publisert/271a0ab7-3231-44c9-bb7e-69d273466bc0',
        type: 'external' as any,
      },
    ];
  }, [intl?.formatMessage]);

  const socialLinks = useMemo(() => {
    return [
      {
        text: intl.formatMessage({ id: 'footer.social.facebook' }),
        url: 'https://www.facebook.com/sjofartsdir',
        type: 'facebook' as any,
      },
      {
        text: intl.formatMessage({ id: 'footer.social.instagram' }),
        url: 'https://www.instagram.com/sjofartsdir/',
        type: 'instagram' as any,
      },
      {
        text: intl.formatMessage({ id: 'footer.social.linkedin' }),
        url: 'https://www.linkedin.com/company/41031/',
        type: 'linkedin' as any,
      },
    ];
  }, [intl?.formatMessage]);

  const menuItems: any[] = useMemo(() => {
    const items: any[] = [];
    if (showCertificate || showEndorsement) {
      items.push({
        icon: <SdiLicense color={styles.colors.primary} size="s" />,
        text: intl.formatMessage({ id: 'certificateDashboard.page.title' }),
        type: 'button',
        onClick: () => {
          navigateDirectlyByUrl(`#${routes.certificateOverview}`);
        },
      });
    }
    if (showApplications) {
      items.push({
        icon: <SdiLicense color={styles.colors.primary} size="s" />,
        text: intl.formatMessage({ id: 'applicationsOverview.page.title' }),
        type: 'button',
        onClick: () => {
          navigateDirectlyByUrl(`#${routes.applicationsOverview}`);
        },
      });
    }
    items.push({
      icon: <SdiShip color={styles.colors.primary} size="s" />,
      text: intl.formatMessage({ id: 'competenceDashboard.page.title' }),
      type: 'button',
      onClick: () => {
        navigateDirectlyByUrl(`#${routes.competenceDashboard}`);
      },
    });
    if (hasValidPnr && showSid) {
      items.push({
        icon: <SdiLicense color={styles.colors.primary} size="s" />,
        text: intl.formatMessage({ id: 'sidDashboard.page.title' }),
        type: 'button',
        onClick: () => {
          navigateDirectlyByUrl(`#${routes.sidDashboard}`);
        },
      });
    }
    if (showPayment && routes.paymentsolution) {
      items.push({
        icon: <SdiDashboard color={styles.colors.primary} size="s" />,
        text: intl.formatMessage({ id: 'home.tile.paymentSolution' }),
        type: 'a',
        url: routes.paymentsolution,
      });
    }
    if (showProfile) {
      items.push({
        icon: <SdiProfile color={styles.colors.primary} size="s" />,
        text: intl.formatMessage({ id: 'home.tile.myprofile' }),
        type: 'button',
        onClick: () => {
          navigateDirectlyByUrl(`#${routes.profile}`);
        },
      });
    }
    return items;
  }, [intl, hasValidPnr, showCertificate, showPayment, showProfile, showEndorsement, showSid]);

  return (
    <>
      <SkipLink
        linkText={intl.formatMessage({ id: 'userbar.skiplink' })}
        mainContentId="mss-main"
      />
      <SdirHeader
        appName={intl.formatMessage({ id: 'home.page.title' })}
        rootUrl={intl.locale === DEFAULT_LANG ? '/' : `/#/${intl.locale}`}
        hasLogin
        backLink={{
          url: 'https://sdir.no',
          text: intl.formatMessage({ id: 'userbar.sdirlink' }),
        }}
        isLoggedIn
        loggedInAs={{
          username: currentUser?.profile?.name || '',
          identification: currentUser?.profile?.reportee || '',
        }}
        loggedInAsTitle={loggedInAsTitle}
        loggedInAsDropdownInfo={loggedInAsDropdownInfo}
        languageOptions={showLanguage ? languageOptions : undefined}
        onChangeLanguage={onChangeLanguage}
        selectedLanguage={intl.locale || DEFAULT_LANG}
        maxContentWidth="1490px"
        menuToggleText={intl.formatMessage({ id: 'userbar.menu' })}
        navMenuItems={menuItems}
        onLogOutClick={onLogout}
      />
      <AppContainer
        id="mss-main"
        data-testid="mss-app-container"
        className="extended-layout"
        tabIndex={-1}
      >
        {children}
      </AppContainer>
      <SdirFooter
        tagline={intl.formatMessage({ id: 'footer.tagline' })}
        maxContentWidth="1490px"
        languageOptions={showLanguage ? languageOptions : undefined}
        onChangeLanguage={onChangeLanguage}
        selectedLanguage={intl.locale || DEFAULT_LANG}
        aboutHeader={intl.formatMessage({ id: 'footer.about.title' })}
        aboutLinks={aboutLinks}
        socialHeader={intl.formatMessage({ id: 'footer.social.title' })}
        socialLinks={socialLinks}
      />
    </>
  );
};

const AppContainer = styled.main`
  margin: 0 auto 0 auto;
  background-color: ${({ theme }) => theme.colors.background.primary};

  footer ul {
    padding-left: 0;
  }

  &.extended-layout {
    max-width: 1490px;
    min-height: 700px;
    padding: 0 150px 20px 150px;

    @media screen and (max-width: ${({ theme }) => theme.screen.xLarge}) {
      padding: 0 15px 15px 15px;
    }
  }
`;
